import {
  collection,
  doc,
  setDoc,
  getDoc,
  addDoc,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "./firebase";

// Conversation doc lives at conversations/{clientUid}
// so clients always know their own path, and admin queries all.

export async function getOrCreateConversation(clientId, clientName, clientAvatar) {
  const ref = doc(db, "conversations", clientId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      clientId,
      clientName: clientName || "",
      clientAvatar: clientAvatar || "",
      lastMessage: "",
      lastMessageAt: null,
      clientUnread: 0,
      coachUnread: 0,
    });
  }
  const fresh = await getDoc(ref);
  return { id: clientId, ...fresh.data() };
}

export async function sendMessage({ conversationId, senderId, senderName, senderAvatar, text, attachments, isFromCoach }) {
  const cleaned = (text || "").trim();
  const hasAttachments = (attachments || []).length > 0;
  if (!cleaned && !hasAttachments) return;
  const plainText = cleaned.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

  await addDoc(collection(db, "conversations", conversationId, "messages"), {
    senderId,
    senderName: senderName || "",
    senderAvatar: senderAvatar || "",
    text: cleaned,
    attachments: attachments || [],
    createdAt: serverTimestamp(),
  });

  await updateDoc(doc(db, "conversations", conversationId), {
    lastMessage: plainText || (hasAttachments ? (attachments[0].type === "image" ? "📷 Image" : "🔗 Link") : ""),
    lastMessageAt: serverTimestamp(),
    ...(isFromCoach ? { clientUnread: increment(1) } : { coachUnread: increment(1) }),
  });
}

export function subscribeToMessages(conversationId, callback) {
  const q = query(
    collection(db, "conversations", conversationId, "messages"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, (err) => console.error("subscribeToMessages:", err));
}

export function subscribeToConversation(conversationId, callback) {
  return onSnapshot(doc(db, "conversations", conversationId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
    else callback(null);
  }, (err) => console.error("subscribeToConversation:", err));
}

export function subscribeToAllConversations(callback) {
  return onSnapshot(collection(db, "conversations"), (snap) => {
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => {
      const aT = a.lastMessageAt?.toDate?.()?.getTime?.() || 0;
      const bT = b.lastMessageAt?.toDate?.()?.getTime?.() || 0;
      return bT - aT;
    });
    callback(list);
  }, (err) => console.error("subscribeToAllConversations:", err));
}

export async function toggleMessageReaction({ conversationId, messageId, emoji, uid }) {
  const msgRef = doc(db, "conversations", conversationId, "messages", messageId);
  const snap = await getDoc(msgRef);
  if (!snap.exists()) return;
  const current = snap.data().reactions?.[emoji] || [];
  const hasReacted = current.includes(uid);
  await updateDoc(msgRef, {
    [`reactions.${emoji}`]: hasReacted ? arrayRemove(uid) : arrayUnion(uid),
  });
}

export async function markConversationRead(conversationId, isCoach) {
  try {
    await updateDoc(doc(db, "conversations", conversationId), {
      ...(isCoach ? { coachUnread: 0 } : { clientUnread: 0 }),
    });
  } catch {}
}
